import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { KpiTile, PageHeader, SectionCard, StatusBadge } from '@striker/ui-shell';
import { apiFetch } from '../auth/session';

type SosTicket = {
  id: string;
  store_name: string | null;
  email: string;
  status: string;
  is_emergency: boolean;
  critical_block: string | null;
  contact_phone: string | null;
  message: string;
  created_at: string;
};

type StatusFilter = 'emergency_open' | 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'all';

export function MasterSosPage() {
  const [tickets, setTickets] = useState<SosTicket[]>([]);
  const [openCount, setOpenCount] = useState(0);
  const [filter, setFilter] = useState<StatusFilter>('emergency_open');
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    const qs =
      filter === 'emergency_open'
        ? 'is_emergency=1'
        : filter === 'all'
          ? 'limit=80'
          : `status=${encodeURIComponent(filter)}&limit=80`;
    const [countRes, listRes] = await Promise.all([
      apiFetch('/api/v1/master/support/sos-tickets/open-count'),
      apiFetch(`/api/v1/master/support/sos-tickets?${qs}`),
    ]);
    if (!countRes.ok || !listRes.ok) {
      setError('Impossibile caricare i ticket SOS');
      return;
    }
    const countBody = (await countRes.json()) as { open_count?: number };
    const listBody = (await listRes.json()) as SosTicket[];
    setOpenCount(Number(countBody.open_count || 0));
    setTickets(Array.isArray(listBody) ? listBody : []);
  }, [filter]);

  useEffect(() => {
    void load();
  }, [load]);

  const criticalOpen = useMemo(
    () => tickets.filter((t) => t.is_emergency && t.status === 'OPEN').length,
    [tickets],
  );

  async function patchStatus(id: string, status: string) {
    setBusyId(id);
    setError(null);
    try {
      const res = await apiFetch(`/api/v1/master/support/sos-tickets/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(body.message || `Patch failed (${res.status})`);
      }
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section className="sk-mc-page">
      <PageHeader
        title="Supporto · SOS"
        subtitle={
          <>
            <Link to="/master">← Master</Link> ·{' '}
            <Link to="/master/support">Inbox ticket tenant</Link> · emergenze pubbliche
            (`core_master`)
          </>
        }
      />

      <div className="sk-mc-grid sk-mc-grid--kpi">
        <KpiTile label="SOS aperti" value={openCount} tone={openCount > 0 ? 'error' : 'ok'} icon={<span>🚨</span>} />
        <KpiTile label="In vista" value={tickets.length} tone="info" icon={<span>📋</span>} />
        <KpiTile label="Critici OPEN" value={criticalOpen} tone={criticalOpen > 0 ? 'error' : 'ok'} icon={<span>⚠</span>} />
      </div>

      {error ? <p className="sk-error">{error}</p> : null}

      <SectionCard title="Filtri">
        <label className="sk-field" style={{ maxWidth: 280 }}>
          <span>Filtro</span>
          <select
            className="sk-input"
            value={filter}
            onChange={(e) => setFilter(e.target.value as StatusFilter)}
          >
            <option value="emergency_open">Emergenze (default)</option>
            <option value="OPEN">Stato OPEN</option>
            <option value="IN_PROGRESS">In lavorazione</option>
            <option value="RESOLVED">Risolti</option>
            <option value="all">Tutti</option>
          </select>
        </label>
      </SectionCard>

      <div className="sk-saas-split">
        {tickets.map((ticket) => (
          <SectionCard key={ticket.id}>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem' }}>
              <StatusBadge status={ticket.status} />
              {ticket.is_emergency ? <span className="sk-mc-badge sk-mc-badge--error">EMERGENCY</span> : null}
              {ticket.critical_block ? <span className="sk-mc-badge sk-mc-badge--warn">{ticket.critical_block}</span> : null}
            </div>
            <h2 className="sk-mc-card__title" style={{ marginBottom: '0.4rem' }}>
              {ticket.store_name || 'Tenant'} · {ticket.email}
            </h2>
            <p className="sk-mc-hint" style={{ whiteSpace: 'pre-wrap' }}>
              {ticket.message}
            </p>
            <p className="sk-mc-hint" style={{ marginTop: '0.5rem' }}>
              Tel:{' '}
              {ticket.contact_phone ? (
                <a href={`tel:${ticket.contact_phone}`}>{ticket.contact_phone}</a>
              ) : (
                '—'
              )}{' '}
              · {ticket.created_at ? new Date(ticket.created_at).toLocaleString('it-IT') : ''}
            </p>
            <div style={{ display: 'flex', gap: '0.35rem', marginTop: '0.75rem' }}>
              {ticket.status === 'OPEN' ? (
                <button
                  type="button"
                  className="sk-btn"
                  disabled={busyId === ticket.id}
                  onClick={() => void patchStatus(ticket.id, 'IN_PROGRESS')}
                >
                  In lavorazione
                </button>
              ) : null}
              {ticket.status !== 'RESOLVED' ? (
                <button
                  type="button"
                  className="sk-btn"
                  disabled={busyId === ticket.id}
                  onClick={() => void patchStatus(ticket.id, 'RESOLVED')}
                >
                  Risolvi
                </button>
              ) : null}
            </div>
          </SectionCard>
        ))}
      </div>
      {!tickets.length ? <p className="sk-mc-hint">Nessun ticket SOS in questa vista.</p> : null}
    </section>
  );
}
